"use client";

import * as React from "react";
import { BrainIcon, ChevronDown } from "lucide-react";
import type { ComponentProps, ReactNode } from "react";
import {
	createContext,
	memo,
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState,
} from "react";
import { Streamdown } from "streamdown";

import { cn } from "../../lib/utils";
import {
	Collapsible,
	CollapsibleContent,
	CollapsibleTrigger,
} from "../ui/collapsible";
import { formatDuration } from "./format-duration";
import { isRelativeLinkOnly, type IsInternalLink } from "./link-safety";
import {
	Accordion,
	AccordionContent,
	AccordionItem,
	AccordionTrigger,
} from "../ui/accordion";
import { ChatLoader, THINKING_MARK_SIZE } from "./loader";
import { MARKDOWN_CHROME } from "./markdown-chrome";
import { MarkdownControlTooltips } from "./markdown-control-tooltips";
import { toReasoningSteps } from "./reasoning-steps";
import { ChatShimmer } from "./shimmer";

/**
 * Minimal controllable-state hook.
 *
 * Local rather than `@radix-ui/react-use-controllable-state` so this package
 * does not depend on a module it only reaches transitively today.
 *
 * Like radix's, it ignores `defaultProp` changes after mount — a controlled
 * value is the only way to drive it from outside. When a caller needs the
 * default to take effect again (a finished answer starting to regenerate), it
 * remounts the panel with a `key`, which is what `ChatMessage` does.
 */
function useControllableState<T>({
	prop,
	defaultProp,
	onChange,
}: {
	prop?: T;
	defaultProp: T;
	onChange?: (value: T) => void;
}): [T, (value: T) => void] {
	const [uncontrolled, setUncontrolled] = useState<T>(defaultProp);
	const isControlled = prop !== undefined;
	const value = isControlled ? (prop as T) : uncontrolled;

	const setValue = useCallback(
		(next: T) => {
			if (!isControlled) {
				setUncontrolled(next);
			}
			onChange?.(next);
		},
		[isControlled, onChange],
	);

	return [value, setValue];
}

interface ReasoningContextValue {
	isStreaming: boolean;
	isOpen: boolean;
	setIsOpen: (open: boolean) => void;
	duration: number | undefined;
	/** How many steps the content will render, for the summary line. */
	stepCount: number | undefined;
	/** The step running right now, named on the summary line while streaming. */
	currentStep: string | undefined;
	/** Whether the steps are rendered live, which decides if there is anything to expand. */
	showStepsWhileStreaming: boolean;
}

const ReasoningContext = createContext<ReasoningContextValue | null>(null);

export const useReasoning = () => {
	const context = useContext(ReasoningContext);
	if (!context) {
		throw new Error("Reasoning components must be used within a Reasoning");
	}
	return context;
};

export type ReasoningProps = Omit<
	ComponentProps<typeof Collapsible>,
	"open" | "defaultOpen" | "onOpenChange"
> & {
	isStreaming?: boolean;
	open?: boolean;
	defaultOpen?: boolean;
	onOpenChange?: (open: boolean) => void;
	/** Known elapsed seconds. Omitted lets the panel time its own streaming run. */
	duration?: number;
	/**
	 * How many steps the content holds.
	 *
	 * Passed in rather than counted here: the trigger renders above the content and
	 * cannot see it, and the count has to match the rows exactly — blank and
	 * repeated steps are dropped before rendering, so a raw `parts.length` would
	 * disagree with what the reader can count on screen.
	 */
	stepCount?: number;
	/**
	 * The step running right now.
	 *
	 * Named on the summary line while streaming, in place of a bare "Thinking...".
	 * Passed in for the same reason `stepCount` is: the trigger renders above the
	 * content and cannot see the steps itself.
	 */
	currentStep?: string;
	/**
	 * Show every step as it arrives, instead of only the summary line naming the
	 * current one.
	 *
	 * Off, because stacking is what the criterion rules out: the box grew for as
	 * long as the run lasted, moving the page under whoever was reading it, and it
	 * kept every contradiction on screen - one run asked the same question four
	 * times and reported two different fund counts, all of it sitting there for the
	 * full 106 seconds. Replaced in place, only the latest line is ever visible.
	 *
	 * The history is not lost, only deferred: it is all here the moment the run
	 * settles, behind the summary that counts it.
	 *
	 * On the panel rather than on the content because it decides two things - what
	 * the content draws, and whether the trigger has anything to offer a chevron
	 * for. Split across the two, a caller could turn the steps on and still get a
	 * panel that looked like it had nothing in it.
	 */
	showStepsWhileStreaming?: boolean;
};

const AUTO_CLOSE_DELAY_MS = 1000;

export const Reasoning = memo(
	({
		className,
		isStreaming = false,
		currentStep,
		showStepsWhileStreaming = false,
		open,
		defaultOpen = true,
		onOpenChange,
		duration: durationProp,
		stepCount,
		children,
		...props
	}: ReasoningProps) => {
		const [isOpen, setIsOpen] = useControllableState({
			prop: open,
			defaultProp: defaultOpen,
			onChange: onOpenChange,
		});
		const [timedDuration, setTimedDuration] = useState<number | undefined>(
			undefined,
		);
		const [hasAutoClosed, setHasAutoClosed] = useState(false);
		const startTimeRef = useRef<number | null>(null);

		const duration = durationProp ?? timedDuration;

		useEffect(() => {
			if (isStreaming) {
				if (startTimeRef.current === null) {
					startTimeRef.current = Date.now();
				}
				return;
			}
			if (startTimeRef.current === null) {
				return;
			}
			setTimedDuration(Math.ceil((Date.now() - startTimeRef.current) / 1000));
			startTimeRef.current = null;
		}, [isStreaming]);

		// Collapse once, shortly after the thinking finishes, so the answer gets the
		// space — but only for a panel that opened itself.
		useEffect(() => {
			if (!defaultOpen || isStreaming || !isOpen || hasAutoClosed) {
				return;
			}

			const timer = setTimeout(() => {
				setIsOpen(false);
				setHasAutoClosed(true);
			}, AUTO_CLOSE_DELAY_MS);

			return () => clearTimeout(timer);
		}, [isStreaming, isOpen, defaultOpen, hasAutoClosed, setIsOpen]);

		return (
			<ReasoningContext.Provider
				value={{
					isStreaming,
					isOpen,
					setIsOpen,
					duration,
					stepCount,
					currentStep,
					showStepsWhileStreaming,
				}}
			>
				<Collapsible
					className={cn("not-prose mb-4", className)}
					onOpenChange={setIsOpen}
					open={isOpen}
					{...props}
				>
					{children}
				</Collapsible>
			</ReasoningContext.Provider>
		);
	},
);
Reasoning.displayName = "Reasoning";

export type ReasoningTriggerProps = ComponentProps<
	typeof CollapsibleTrigger
> & {
	/** Override the summary line, e.g. to name the step currently running. */
	getThinkingMessage?: (
		isStreaming: boolean,
		duration?: number,
		stepCount?: number,
		currentStep?: string,
	) => ReactNode;
	/** Override the leading icon. Receives the streaming flag, since it changes. */
	getIcon?: (isStreaming: boolean) => ReactNode;
};

/**
 * The brand mark while thinking, a static brain once it has finished.
 *
 * The spinning mark is the product's "working" signal — the pending phase already
 * uses it — and this line is the same claim in a different phase, so it should
 * look the same. It must NOT keep spinning for "Thought for 14 seconds": nothing
 * is happening by then, and a spinner there reads as a stuck request.
 */
const defaultGetIcon = (isStreaming: boolean) =>
	isStreaming ? (
		<ChatLoader className="shrink-0" size={THINKING_MARK_SIZE} />
	) : (
		// Matched to the spinner it replaces, so the row does not jolt when the turn
		// settles and the mark swaps for the brain.
		<BrainIcon className="size-[18px] shrink-0" />
	);

/** The dot between the step count and the elapsed time. */
const SummaryDot = () => (
	<span
		aria-hidden
		className="inline-block size-1 shrink-0 rounded-full bg-current"
	/>
);

const defaultGetThinkingMessage = (
	isStreaming: boolean,
	duration?: number,
	stepCount?: number,
	currentStep?: string,
) => {
	if (isStreaming) {
		// The step running right now, not a stack of every step that has run. The
		// criterion asks for "a compact inline indicator naming the current step",
		// and this row is that indicator: one line, replaced as the run moves on, so
		// the page does not grow under someone reading it. "Thinking..." until the
		// first step has a name.
		return (
			<ChatShimmer duration={1}>{currentStep || "Thinking..."}</ChatShimmer>
		);
	}

	// Nothing to count means nothing to summarise, so fall back to the prose form.
	if (!stepCount) {
		return (
			<p>
				{duration === undefined
					? "Thought for a few seconds"
					: `Thought for ${formatDuration(duration)}`}
			</p>
		);
	}

	const steps = `${stepCount} ${stepCount === 1 ? "step" : "steps"}`;

	// Compact seconds, not `formatDuration`'s prose: this line is a label beside a
	// count, and "3 steps • Thought for 11 seconds" reads as two competing sentences.
	return (
		<p className="flex items-center gap-1.5">
			{steps}
			{duration !== undefined && (
				<>
					<SummaryDot />
					{`${duration}s`}
				</>
			)}
		</p>
	);
};

export const ReasoningTrigger = memo(
	({
		className,
		children,
		getThinkingMessage = defaultGetThinkingMessage,
		getIcon = defaultGetIcon,
		...props
	}: ReasoningTriggerProps) => {
		const {
			isStreaming,
			isOpen,
			duration,
			stepCount,
			currentStep,
			showStepsWhileStreaming,
		} = useReasoning();
		// While the run is going the panel holds nothing: the summary line names the
		// current step and the steps themselves are not rendered until it settles. So
		// there is nothing to expand, and a chevron that opens an empty panel reads as
		// broken - which is exactly how it was reported.
		const canExpand = !isStreaming || showStepsWhileStreaming;

		return (
			<CollapsibleTrigger
				className={cn(
					"flex w-full items-center gap-2 text-neutral-500 text-sm transition-colors",
					canExpand && "hover:text-neutral-950 dark:hover:text-neutral-50",
					"dark:text-neutral-400",
					className,
				)}
				// Not merely un-chevroned: left live it would still be a focus stop that
				// swallowed a press, and the stray toggle would decide whether the panel
				// came back open when the run settled.
				disabled={!canExpand}
				{...props}
			>
				{children ?? (
					<>
						{getIcon(isStreaming)}
						{getThinkingMessage(isStreaming, duration, stepCount, currentStep)}
						{canExpand && (
							<ChevronDown
								className={cn(
									"size-4 transition-transform",
									isOpen ? "rotate-180" : "rotate-0",
								)}
							/>
						)}
					</>
				)}
			</CollapsibleTrigger>
		);
	},
);
ReasoningTrigger.displayName = "ReasoningTrigger";

export type ReasoningContentProps = Omit<
	ComponentProps<typeof CollapsibleContent>,
	"children"
> & {
	/**
	 * The chain of thought.
	 *
	 * An array renders one accordion row per step. A single string still works and
	 * becomes one row, so existing callers are unaffected.
	 */
	children: string | string[];
	allowLink?: IsInternalLink;
};

export const ReasoningContent = memo(
	({
		className,
		children,
		allowLink = isRelativeLinkOnly,
		...props
	}: ReasoningContentProps) => {
		const { isStreaming, showStepsWhileStreaming } = useReasoning();
		const steps = toReasoningSteps(children);

		// Nothing below the summary line while the run is going: the line itself names
		// the current step, and that is the whole indicator. Returning null rather than
		// an empty panel keeps the row's height fixed from the first step to the last.
		if (isStreaming && !showStepsWhileStreaming) {
			return null;
		}

		return (
			<CollapsibleContent
				className={cn(
					"mt-4 text-sm",
					"data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-top-2 data-[state=open]:slide-in-from-top-2 text-neutral-500 outline-none data-[state=closed]:animate-out data-[state=open]:animate-in dark:text-neutral-400",
					className,
				)}
				{...props}
			>
				{/* One row per step, rather than one blob split by markdown rules. The steps
				    were always separate — `buildReasoningText` used to join them with `---`,
				    which is what drew the horizontal lines. */}
				{/* A bordered card spanning the answer column, with the steps separated by
				    rules inside it. `w-full` is not decorative: the assistant message used
				    to be `w-fit`, which sized this to the shortest line in the answer. */}
				<Accordion
					className="w-full overflow-hidden rounded-lg border border-neutral-200 dark:border-neutral-800"
					type="multiple"
				>
					{steps.map((step, index) =>
						step.body === null ? (
							// Nothing to reveal, so no trigger and no chevron: a row that opens
							// onto an empty panel is worse than a row that plainly does not open.
							<div
								className="border-neutral-200 border-b px-3 py-2.5 last:border-b-0 dark:border-neutral-800"
								key={index}
							>
								{step.label}
							</div>
						) : (
							<AccordionItem key={index} value={String(index)}>
								<AccordionTrigger className="px-3 py-2.5 font-normal hover:no-underline">
									{step.label}
								</AccordionTrigger>
								<AccordionContent className="px-3 pb-3">
									<MarkdownControlTooltips>
										<Streamdown
											// The same plain fade the answer gets, and for the same reason - see the
											// note on `MessageResponse`. A reasoning step is streamed content on the
											// same page, so it was carrying the same 350ms blur and the same §9
											// deviation comment; APP-27605 settled both at once.
											animated={{
												animation: "fadeIn",
												duration: 150,
												stagger: 60,
												easing: "cubic-bezier(0.22, 1, 0.36, 1)",
											}}
											// The same chrome the answer gets. A reasoning step can hold a table
											// too, and it used to render with Streamdown's unstyled borders.
											className={MARKDOWN_CHROME}
											isAnimating={isStreaming}
											linkSafety={{ enabled: true, onLinkCheck: allowLink }}
										>
											{step.body}
										</Streamdown>
									</MarkdownControlTooltips>
								</AccordionContent>
							</AccordionItem>
						),
					)}
				</Accordion>
			</CollapsibleContent>
		);
	},
);
ReasoningContent.displayName = "ReasoningContent";
